import { ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"

interface ListPaginationProps {
  /** Current page number (1-indexed). */
  page: number
  /** Items per page — used to compute total page count alongside `total`. */
  perPage: number
  /** Total number of rows across all pages. */
  total: number
  /**
   * Fires with the next page number when the user clicks Previous / Next.
   * Caller routes via `router.get(currentPath, { page: nextPage }, { ... })`.
   */
  onPageChange: (page: number) => void
  /**
   * Test ID override for non-default consumers (e.g. the audit-history
   * panel inside Blocklist uses `events_page`). Defaults to
   * `admin-list-pagination`.
   */
  testId?: string
  className?: string
}

/**
 * ListPagination — prev/next pagination footer for admin list pages.
 *
 * Page-jump (numbered list / first-last buttons) is intentionally out of
 * scope for v1 per the OKS-15 TRD §State decision — prev/next only,
 * keyboard-accessible via standard Button focus order.
 *
 * The component is a pure controlled affordance: the parent owns the
 * `page` state via URL params; this component just renders + dispatches.
 *
 * When the browser reports offline (`useOnline === false`), both buttons
 * are disabled — the URL change would trigger an Inertia partial reload
 * that can't currently complete.
 *
 * Test IDs:
 *   - Container: `admin-list-pagination` (locked v6 testid contract).
 *   - Buttons (programmatic): `admin-list-pagination-{prev|next}`.
 */
export function ListPagination({
  page,
  perPage,
  total,
  onPageChange,
  testId = "admin-list-pagination",
  className,
}: ListPaginationProps) {
  const { t } = useTranslation()
  const online = useOnline()

  const totalPages = Math.max(1, Math.ceil(total / perPage))
  const hasPrev = page > 1
  const hasNext = page < totalPages

  // Don't render the footer at all when there's only one page of results
  // — the bare "Page 1 of 1" caption + disabled buttons are visual noise.
  if (totalPages <= 1) return null

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        "flex items-center justify-between gap-3 border-t pt-4",
        className,
      )}
      data-testid={testId}
    >
      <span className="text-muted-foreground text-sm">
        {t("admin.shared.page_of", { page, total_pages: totalPages })}
      </span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasPrev || !online}
          aria-disabled={!hasPrev || !online ? "true" : undefined}
          onClick={() => hasPrev && onPageChange(page - 1)}
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          <span>{t("admin.shared.previous")}</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasNext || !online}
          aria-disabled={!hasNext || !online ? "true" : undefined}
          onClick={() => hasNext && onPageChange(page + 1)}
          data-testid={`${testId}-next`}
        >
          <span>{t("admin.shared.next")}</span>
          <ChevronRight className="size-4" aria-hidden="true" />
        </Button>
      </div>
    </nav>
  )
}
